import { useCallback, useEffect, useState } from "react";
import { Box, Card, Skeleton, Typography } from "@mui/material";
import {
  MiniMap,
  Node,
  ReactFlow,
  useNodesInitialized,
  useReactFlow,
} from "@xyflow/react";
import { useParams } from "react-router-dom";
import __ from "src/utils/Translations";
import useOrgchartAreasStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Areas/store";
import Controls from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Controls/Controls";
import "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/MiniMap/_minimap.scss";
import {
  reactFlowSettings,
  setCenterOptions,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Areas/OrgchartTree/OrgchartSettings";
// eslint-disable-next-line
import {
  getAutoLayoutWorker,
  getFindNodeWorker,
} from "../../workers/getOrgchartWebWorker";

const AutoLayoutWorker: Worker | undefined = getAutoLayoutWorker();
const FindNodeWorker: Worker | undefined = getFindNodeWorker();

const OrgchartTree = () => {
  const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
  const flow = useReactFlow();
  const { nodeId: urlNodeId } = useParams() as { nodeId: string | undefined };

  const handleChangeFullScreenMode = () => {
    setFullScreenMode(!fullScreenMode);
  };

  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    allNodes,
    searchedNodeId,
    setSearchedNodeId,
    wasSearchedNodeCleared,
  } = useOrgchartAreasStore();
  const nodesInitialized = useNodesInitialized();

  const initialLayout = useCallback(() => {
    if (!searchedNodeId) {
      return null;
    }

    AutoLayoutWorker.postMessage({ nodes, edges });
    AutoLayoutWorker.onmessage = (event) => {
      setNodes(event.data.nodes);
      setEdges(event.data.edges);
      return null;
    };

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges, nodes, allNodes, searchedNodeId]);

  useEffect(() => {
    if (allNodes.length && !searchedNodeId) {
      initialLayout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNodes, searchedNodeId]);

  useEffect(() => {
    if (
      useOrgchartAreasStore.getState().nodeToFocusOn &&
      typeof useOrgchartAreasStore.getState().nodeToFocusOn !== "undefined" &&
      searchedNodeId === null
    ) {
      const position = flow.getNode(
        `${useOrgchartAreasStore.getState()?.nodeToFocusOn?.id}`
      )?.position;
      const node = flow.getNode(
        `${useOrgchartAreasStore.getState()?.nodeToFocusOn?.id}`
      ) as Node;

      const height = node?.measured?.height;
      const width = node?.measured?.width;

      if (
        useOrgchartAreasStore.getState().nodeToFocusOn &&
        useOrgchartAreasStore.getState().nodeToFocusOn?.isCollapsed &&
        position &&
        width &&
        height
      ) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            position.x + width / 2,
            position.y + height / 2,
            setCenterOptions
          );
        });
      } else if (
        useOrgchartAreasStore.getState().nodeToFocusOn &&
        !useOrgchartAreasStore.getState().nodeToFocusOn?.isCollapsed &&
        position &&
        width &&
        height
      ) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            position.x + width / 2,
            position.y + 50 + height * 2,
            setCenterOptions
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  useEffect(() => {
    if (searchedNodeId !== null) {
      FindNodeWorker.postMessage({ allNodes, searchedNodeId });
      FindNodeWorker.onmessage = (fEvent) => {
        AutoLayoutWorker.postMessage({ nodes: fEvent.data.nodes, edges });
        AutoLayoutWorker.onmessage = (aEvent) => {
          setNodes(aEvent.data.nodes);
          setEdges(aEvent.data.edges);
        };
        useOrgchartAreasStore
          .getState()
          .childrenCollapsedStatus.forEach((_value, key) => {
            if (
              fEvent.data.searchRoute.find((routeId: string) => routeId === key)
            ) {
              useOrgchartAreasStore
                .getState()
                .setChildrenCollapsedStatus(
                  new Map(
                    useOrgchartAreasStore.getState().childrenCollapsedStatus
                  ).set(key, false)
                );
            } else {
              useOrgchartAreasStore
                .getState()
                .setChildrenCollapsedStatus(
                  new Map(
                    useOrgchartAreasStore.getState().childrenCollapsedStatus
                  ).set(key, true)
                );
            }
          });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedNodeId]);

  useEffect(() => {
    if (searchedNodeId && nodesInitialized) {
      const searchedNode = flow.getNode(searchedNodeId);
      const height = searchedNode?.measured?.height;
      const width = searchedNode?.measured?.width;
      if (searchedNode && height && width) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            searchedNode.position.x + width / 2,
            searchedNode.position.y + height / 2,
            setCenterOptions
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedNodeId, nodesInitialized, nodes]);

  useEffect(() => {
    if (flow.viewportInitialized) {
      window.requestAnimationFrame(() => {
        flow.fitView({ maxZoom: 0.7 });
      });
    }
  }, [flow, fullScreenMode]);

  useEffect(() => {
    if (nodesInitialized) {
      AutoLayoutWorker.postMessage({ nodes, edges });
      AutoLayoutWorker.onmessage = (aEvent) => {
        setNodes(aEvent.data.nodes);
        setEdges(aEvent.data.edges);
      };

      if (urlNodeId && !wasSearchedNodeCleared) {
        setSearchedNodeId(urlNodeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesInitialized]);

  return (
    <Card
      data-testid="os__view-orgchart"
      sx={
        fullScreenMode
          ? {
              position: "fixed",
              height: "100vh",
              width: "100vw",
              left: 0,
              top: "60px",
              zIndex: 1000000,
              padding: 0,
            }
          : {
              margin: "16px 0 0 0",
              padding: "0",
              height: "641px",
              width: "100%",
              left: 0,
              top: 0,
            }
      }
    >
      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          padding: "8px 16px",
          borderBottom: "1px solid #A1DEFA",
        }}
      >
        <Typography sx={{ fontWeight: "fontWeightMedium" }}>
          {__(`Struktura`)}
        </Typography>
      </Box>
      <Box
        sx={{
          height: fullScreenMode ? "calc(100vh - 101px)" : "600px",
          position: "relative",
          contentVisibility: "auto",
        }}
      >
        {!nodesInitialized && <Skeleton variant="rounded" height="600px" />}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          {...reactFlowSettings}
        >
          <Controls setFullScreenMode={handleChangeFullScreenMode} />
          {nodes.length <= 60 && (
            <MiniMap
              pannable
              maskColor="#86868640"
              style={{ borderRadius: "5px" }}
            />
          )}
        </ReactFlow>
      </Box>
    </Card>
  );
};

export default OrgchartTree;
